import ListingDetailsFields, { listingPayload } from "./ListingDetailsFields";
import "../styles/ListingDetailsFields.css";
// src/Components/AddProperty.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api"; // axios instance with token
import "../styles/Property.css";

function AddProperty() {
  const [listingDetails, setListingDetails] = useState({});
  const [propertyName, setPropertyName] = useState("");
  const [location, setLocation] = useState("");
  const [rentPrice, setRentPrice] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [description, setDescription] = useState("");
  const [imageUrlsText, setImageUrlsText] = useState("");
  const [uploadedImageData, setUploadedImageData] = useState([]);
  const [tenantName, setTenantName] = useState("");
  const [isOccupied, setIsOccupied] = useState(false);
  const [leaseStartDate, setLeaseStartDate] = useState("");
  const [leaseEndDate, setLeaseEndDate] = useState("");
  const [noticeDate, setNoticeDate] = useState("");
  const [moveOutDate, setMoveOutDate] = useState("");
  const [renewalStatus, setRenewalStatus] = useState("active");
  const [leaseNotes, setLeaseNotes] = useState("");
  const [toast, setToast] = useState({ message: "", type: "" });

  const navigate = useNavigate();

  const parseImageLines = (value) =>
    String(value || "")
      .split(/\r?\n/)
      .map((item) => item.trim())
      .filter(Boolean);

  const mergeImageValues = (values) =>
    Array.from(new Set(values.map((value) => String(value || "").trim()).filter(Boolean))).slice(0, 12);

  const isDataImage = (value) => /^data:image\//i.test(String(value || ""));

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    try {
      const allowed = files.filter((file) => file.type.startsWith("image/"));
      const encoded = await Promise.all(allowed.map((file) => readFileAsDataUrl(file)));
      setUploadedImageData((prev) => mergeImageValues([...prev, ...encoded]));
    } catch (error) {
      console.error(error);
      setToast({ message: "Failed to read one or more image files.", type: "error" });
      setTimeout(() => setToast({ message: "", type: "" }), 5000);
    } finally {
      event.target.value = "";
    }
  };

  const removePreviewImage = (image) => {
    if (isDataImage(image)) {
      setUploadedImageData((prev) => prev.filter((item) => item !== image));
      return;
    }

    const next = parseImageLines(imageUrlsText).filter((item) => item !== image);
    setImageUrlsText(next.join("\n"));
  };

  const previewImages = mergeImageValues([...uploadedImageData, ...parseImageLines(imageUrlsText)]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Map camelCase → snake_case for Laravel
      await api.post("/rental-properties", {
        listing_details: listingPayload(listingDetails),
        property_name: propertyName,
        location,
        rent_price: Number(rentPrice),
        latitude: latitude ? Number(latitude) : null,
        longitude: longitude ? Number(longitude) : null,
        description,
        images: previewImages,
        tenant_name: isOccupied ? tenantName : null,
        is_occupied: isOccupied,
        lease_start_date: leaseStartDate || null,
        lease_end_date: leaseEndDate || null,
        notice_date: noticeDate || null,
        move_out_date: moveOutDate || null,
        renewal_status: renewalStatus || "active",
        lease_notes: leaseNotes || null,
      });

      setToast({ message: "Property added successfully!", type: "success" });

      setTimeout(() => {
        setToast({ message: "", type: "" });
        navigate("/list-properties"); // redirect to property list
      }, 2000);
    } catch (err) {
      console.error("Error adding property:", err);
      const message =
        err.response?.data?.message || "Failed to add property";
      setToast({ message, type: "error" });
      setTimeout(() => setToast({ message: "", type: "" }), 5000);
    }
  };

  return (
    <div className="property-page cozy-background">
      <div className="property-card">
        <button className="home-btn" onClick={() => navigate("/home")}>
          🏠 Home
        </button>

        <h2 className="property-title">Add New Property</h2>

        <form className="property-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="addproperty-field-1">Property Name</label>
            <input id="addproperty-field-1"
              type="text"
              placeholder="Property Name"
              value={propertyName}
              onChange={(e) => setPropertyName(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="addproperty-field-2">Location</label>
            <input id="addproperty-field-2"
              type="text"
              placeholder="Location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="addproperty-field-3">Rent Price</label>
            <input id="addproperty-field-3"
              type="number"
              placeholder="Rent Price"
              value={rentPrice}
              onChange={(e) => setRentPrice(e.target.value)}
              required
            />
          </div>

          <div className="double-inputs">
            <div className="form-group">
              <label htmlFor="addproperty-field-4">Latitude</label>
              <input id="addproperty-field-4"
                type="number" step="any"
                placeholder="Latitude"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="addproperty-field-5">Longitude</label>
              <input id="addproperty-field-5"
                type="number" step="any"
                placeholder="Longitude"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="addproperty-field-6">Description</label>
            <textarea id="addproperty-field-6"
              placeholder="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="addproperty-field-7">Apartment Image URLs</label>
            <textarea id="addproperty-field-7"
              placeholder="One image URL per line"
              value={imageUrlsText}
              onChange={(e) => setImageUrlsText(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="addproperty-field-8">Upload Apartment Images</label>
            <input id="addproperty-field-8" type="file" accept="image/*" multiple onChange={handleImageUpload} />
          </div>

          {previewImages.length > 0 && (
            <div className="form-group">
              <label>Image Preview</label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {previewImages.map((image) => (
                  <div key={image} className="relative overflow-hidden rounded-lg border border-slate-300/40">
                    <img src={image} alt="Apartment preview" className="h-24 w-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removePreviewImage(image)}
                      className="absolute right-1 top-1 rounded bg-black/70 px-2 py-0.5 text-[10px] font-semibold text-white"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <ListingDetailsFields value={listingDetails} onChange={setListingDetails} />
          <h3 className="unit-section-title">Lease Lifecycle</h3>

          <div className="double-inputs">
            <div className="form-group">
              <label htmlFor="addproperty-field-9">Lease Start Date</label>
              <input id="addproperty-field-9"
                type="date"
                value={leaseStartDate}
                onChange={(e) => setLeaseStartDate(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="addproperty-field-10">Lease End Date</label>
              <input id="addproperty-field-10"
                type="date"
                value={leaseEndDate}
                onChange={(e) => setLeaseEndDate(e.target.value)}
              />
            </div>
          </div>

          <div className="double-inputs">
            <div className="form-group">
              <label htmlFor="addproperty-field-11">Notice Date</label>
              <input id="addproperty-field-11"
                type="date"
                value={noticeDate}
                onChange={(e) => setNoticeDate(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="addproperty-field-12">Move-Out Date</label>
              <input id="addproperty-field-12"
                type="date"
                value={moveOutDate}
                onChange={(e) => setMoveOutDate(e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="addproperty-field-13">Renewal Status</label>
            <select id="addproperty-field-13" value={renewalStatus} onChange={(e) => setRenewalStatus(e.target.value)}>
              <option value="active">Active</option>
              <option value="renewal_due">Renewal Due</option>
              <option value="renewed">Renewed</option>
              <option value="move_out_pending">Move-Out Pending</option>
              <option value="ended">Ended</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="addproperty-field-14">Lease Notes</label>
            <textarea id="addproperty-field-14"
              placeholder="Renewal or notice notes"
              value={leaseNotes}
              onChange={(e) => setLeaseNotes(e.target.value)}
            />
          </div>

          <h3 className="unit-section-title">🏘️ Property Occupancy</h3>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={isOccupied}
                onChange={(e) => setIsOccupied(e.target.checked)}
              />
              Occupied
            </label>
          </div>

          {isOccupied && (
            <div className="form-group">
              <label htmlFor="addproperty-field-15">Tenant Name</label>
              <input id="addproperty-field-15"
                type="text"
                placeholder="Tenant name"
                value={tenantName}
                onChange={(e) => setTenantName(e.target.value)}
                required={isOccupied}
              />
            </div>
          )}

          <button type="submit" className="submit-btn">
            Add Property
          </button>
        </form>

        {toast.message && (
          <div className={`toast ${toast.type}`}>
            {toast.type === "success" ? "✅" : "❌"} {toast.message}
          </div>
        )}
      </div>
    </div>
  );
}

export default AddProperty;
